import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { todayIso } from '@mybusinessmate/schema';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { useCreateProject } from '../../lib/projects-api';
import {
  emptyProjectForm,
  toProjectRequest,
  type ProjectFormErrors,
  type ProjectFormValues,
} from '../../lib/project-form';
import { cx } from '../../lib/cx';
import { Banner } from '../ui/banner';
import { buttonClassName } from '../ui/button';
import { DateField } from '../ui/date-field';
import { Drawer } from '../ui/dialog';
import { Input } from '../ui/input';
import { TextArea } from '../ui/text-area';
import { Toggle } from '../ui/toggle';
import { useToast } from '../ui/toast';
import { DomainPicker } from './domain-picker';

export interface NewProjectDrawerProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  /** The owner's first name, for "Javed sees it under Projects in his app". */
  ownerFirstName?: string;
}

const FIELDS: (keyof ProjectFormValues)[] = ['name', 'domain', 'description', 'startDate', 'targetEndDate'];

function Form({
  onClose,
  businessId,
  ownerFirstName,
}: Pick<NewProjectDrawerProps, 'onClose' | 'businessId' | 'ownerFirstName'>) {
  const toast = useToast();
  const navigate = useNavigate();
  const create = useCreateProject();
  const [values, setValues] = useState<ProjectFormValues>(() => ({ ...emptyProjectForm, startDate: todayIso() }));
  const [errors, setErrors] = useState<ProjectFormErrors>({});
  const [formError, setFormError] = useState<string>();

  const set = <K extends keyof ProjectFormValues>(key: K, value: ProjectFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(undefined);
    const result = toProjectRequest(values, businessId);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    try {
      const project = await create.mutateAsync(result.body);
      toast({ title: 'Project created', description: project.name });
      onClose();
      await navigate({ to: '/subdomain/admin/projects/$projectId', params: { projectId: project.id } });
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.length) {
        const fromServer: ProjectFormErrors = {};
        for (const f of e.fieldErrors) {
          if ((FIELDS as string[]).includes(f.field)) fromServer[f.field as keyof ProjectFormValues] = f.message;
        }
        setErrors(fromServer);
      } else if (e.kind === 'forbidden') {
        setFormError('You can’t create projects for this business.');
      } else if (e.kind === 'not_found') {
        setFormError('This business no longer exists.');
      } else if (e.kind === 'unauthorized') {
        setFormError('Your session expired. Please sign in again.');
      } else {
        setFormError('We couldn’t create the project. Please try again.');
      }
    }
  };

  return (
    <form onSubmit={submit} noValidate className="flex min-h-full flex-col gap-(--mbm-space-5)">
      {formError ? <Banner tone="error">{formError}</Banner> : null}

      <Input
        label="Name"
        variant="filled"
        autoComplete="off"
        value={values.name}
        error={errors.name}
        placeholder="e.g. Diwali offer campaign"
        onChange={(e) => set('name', e.target.value)}
      />

      <DomainPicker value={values.domain} onChange={(domain) => set('domain', domain)} error={errors.domain} />

      <TextArea
        label="Description"
        variant="filled"
        rows={3}
        value={values.description}
        error={errors.description}
        onChange={(e) => set('description', e.target.value)}
      />

      <div className="grid grid-cols-2 gap-(--mbm-space-3)">
        <DateField
          label="Starts"
          value={values.startDate}
          error={errors.startDate}
          onChange={(startDate) => set('startDate', startDate)}
        />
        <DateField
          label="Target end"
          value={values.targetEndDate}
          error={errors.targetEndDate}
          placeholder="No date yet"
          onChange={(targetEndDate) => set('targetEndDate', targetEndDate)}
        />
      </div>

      {/* TODO(api): projects have no "visible to client" column yet, so every project shows in the client's Projects
          page. Drawn as designed (on), but it cannot be changed until there is something to save it to. */}
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <div className="flex min-w-0 flex-col gap-(--mbm-space-0-5)">
          <span className="text-label-medium text-text-primary">Visible to client</span>
          <span className="text-body-xsmall text-text-tertiary">
            {ownerFirstName ?? 'The client'} sees it under Projects in their app
          </span>
        </div>
        <Toggle aria-label="Visible to client" checked disabled onCheckedChange={() => {}} />
      </div>

      <p className="rounded-(--mbm-radius-sm) bg-surface-muted px-(--mbm-space-3-5) py-(--mbm-space-3) text-body-small text-text-secondary">
        Milestones and tasks come next, on the project page. Plan and add-on purchases can also create projects
        automatically.
      </p>

      <div className="mt-auto grid grid-cols-2 gap-(--mbm-space-2-5)">
        <button
          type="button"
          onClick={onClose}
          disabled={create.isPending}
          className={cx(
            buttonClassName({ variant: 'ghost', size: 'row' }),
            'h-(--mbm-size-input-filled) border border-border-default',
          )}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          aria-busy={create.isPending || undefined}
          className={cx(buttonClassName({ variant: 'primary', size: 'row' }), 'h-(--mbm-size-input-filled)')}
        >
          Create project
        </button>
      </div>
    </form>
  );
}

/** Figma "New project" drawer (A05.1b). Admin, or the business's account manager (the API enforces it). */
export function NewProjectDrawer({ open, ...rest }: NewProjectDrawerProps) {
  return (
    <Drawer
      size="form"
      open={open}
      onClose={rest.onClose}
      title="New project"
      description={`A container for related tasks at ${rest.businessName}. Progress rolls up from its tasks.`}
    >
      <Form onClose={rest.onClose} businessId={rest.businessId} ownerFirstName={rest.ownerFirstName} />
    </Drawer>
  );
}
